import { vec2, mat2d } from "gl-matrix";

import { rotateMatrixAroundPoint, getRadiansFromAngle } from "../math/Angles";

/**
 * @typedef {import("./handlers").Coordinates} Coordinates
 * @typedef {import("@/types/ElementTypes.mjs").DimensionPosition} DimensionPosition
 */

/**
 * Adds an offset to the scaledRegPoint to recenter. Mutates scaledRegPoint.
 * Mutates the scaled reg point by recentering it to the new shape.
 * @param {Object} props
 * @param {vec2} props.dimensionDiff - width and height diff between original and scaled shape
 * @param {vec2} props.regOffset - regPoint to controlPoint offset, representing the base shape size diagonally
 * @param {vec2} props.scaledRegPoint - reg point after scaling with new size
 * @param {boolean} props.scaleXOnly - scale only the X axis
 * @param {boolean} props.scaleYOnly - scale only the Y axis
 */
const recenterTransform = ({
  dimensionDiff,
  regOffset,
  scaledRegPoint,
  scaleXOnly,
  scaleYOnly,
}) => {
  // sends scaled offset back half width of the tool to appear centered during drag
  const xyOffset = vec2.divide(vec2.create(), dimensionDiff, [2, 2]);

  // direction of manipulation
  const signs = vec2.inverse(vec2.create(), [Math.sign(regOffset[0]), Math.sign(regOffset[1])]);

  // inverse direction of drag (to or from center)
  const signedOffset = vec2.multiply(vec2.create(), xyOffset, signs);

  vec2.add(scaledRegPoint, scaledRegPoint, [
    !scaleYOnly ? signedOffset[0] : 0,
    !scaleXOnly ? signedOffset[1] : 0,
  ]);
};

/**
 * Gets a new xy offset for *translation specifically* during a scale.
 *
 * The trick to this is that a registration point coordinate (either the x, y, or both in a coordinate)
 * that is zero will result in the x or y still being zero. A practical example is dragging the bottom right corner of an element, the registration
 * point will always be 0,0 (as in the top left corner), resulting in a total scale but no movement.
 *
 * In the opposite case where the top left is moved and the bottom right is the registration point, the shape should be scaled (outside of this function),
 * and this will scale the registration point closer or further than it's original sate, resulting in the x,y shift returned.
 *
 * For corners, the mixed zero and non zero values allow this to work by scaling one axis but not another.
 * An example is dragging the bottom left corner: the x can scale but y will not.
 *
 * @todo update to support alt transform
 *
 * @param {Coordinates} regPoint - The point opposite to the manipulated during a transform.
 * @param {mat2d} shapeMatrix - The matrix representing the original shape.
 * @param {vec2} scaledAxes - A scale for x and y. This effectively scales the registration point
 * @param {Pick<DimensionPosition, "w" | "h" | "a">} shapeSize
 * @param {{
 *  dimensionDiff: vec2,
 *  regOffset: vec2,
 *  scaleXOnly: boolean,
 *  scaleYOnly: boolean,
 * }=} symmetricTransform
 * @returns {{
 *  xyOffset: Coordinates,
 *  transformMatrix: tM,
 *  regOffsetResized: vec2
 * }}
 */

export const getScaleTranslation = (
  regPoint,
  shapeMatrix,
  scaledAxes,
  shapeSize,
  symmetricTransform
) => {
  // Creates a matrix with expected width and height, rotated around it's center. This is to translate the scaled registration point for comparison.
  const tM = mat2d.create();
  const angle = getRadiansFromAngle(shapeSize.a);
  rotateMatrixAroundPoint(tM, shapeSize.w / 2, shapeSize.h / 2, angle);

  // registration point translated to the base matrix space
  const transRegPoint = vec2.transformMat2d(vec2.create(), regPoint, shapeMatrix);
  // scale the registration point with scaled axes
  const scaledRegPoint = vec2.multiply(vec2.create(), regPoint, scaledAxes);

  if (symmetricTransform) {
    recenterTransform({ scaledRegPoint, ...symmetricTransform });
  }

  // translate the scaled registration point to the new matrix
  let regOffsetResized = vec2.transformMat2d(vec2.create(), scaledRegPoint, tM);

  // Determine how much the registration point has moved after the resize process.
  return {
    xyOffset: vec2.subtract(regOffsetResized, transRegPoint, regOffsetResized),
    transformMatrix: tM,
    regOffsetResized,
  };
};